import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import type { SaveState } from "@/components/match/use-match-editor";
import {
  CreateStudioNotebookDocument,
  DeleteStudioNotebookDocument,
  SortOrder,
  StudioNotebookCountDocument,
  StudioNotebooksDocument,
  UpdateStudioNotebookDocument,
  type StudioNotebookFieldsFragment,
} from "@/graphql/generated/operations";
import { replaceCard, replaceNote, type StudioCard, type StudioNotebook } from "./notebook-markdown";

export type { StudioNotebook };

const PAGE = 100;

interface Stored {
  book: StudioNotebook;
  // Changed here and not yet confirmed by genesis; a failed save keeps it, and a retry sends it again.
  dirty: boolean;
}

export interface StudioNotebooksState {
  notebooks: StudioNotebook[];
  loading: boolean;
  // The notebooks could not be loaded, or one could not be created or deleted. A failed save of a change is
  // `saveState` instead, with the change still on screen.
  error: boolean;
  // False while loading and after a failed load. Saving does not block: a change shows at once.
  writable: boolean;
  // Saving as the match editor does it (@adr SPARK-0048-studio-notebook-saves-like-the-match-editor@1): `saving` while genesis writes,
  // `error` after a failed write, with the change kept here until `retry` sends it again.
  saveState: SaveState;
  lastSavedAt: number | null;
  retry: () => void;
  createNotebook: (name: string) => Promise<string | null>;
  renameNotebook: (id: string, name: string) => Promise<boolean>;
  deleteNotebook: (id: string) => Promise<boolean>;
  saveText: (notebookId: string, text: string) => Promise<boolean>;
  // Adds a paragraph at the end of the notebook as it stands, so two quick additions both land.
  appendText: (notebookId: string, text: string) => Promise<boolean>;
  // Takes back the note just appended with `text`, only while it is still the notebook's end; false otherwise.
  undoAppend: (notebookId: string, text: string) => Promise<boolean>;
  // Changes one note (the text between two prompts) in place; false when it no longer reads as `expected`.
  editNote: (notebookId: string, segment: number, expected: string, text: string) => Promise<boolean>;
  // Changes one proposal card of a kept prompt in place; false when that card is not there (any more).
  editCard: (notebookId: string, askId: string, cardIndex: number, card: StudioCard) => Promise<boolean>;
}

/** How many notebooks there are, for a tile, without loading any text. */
export function useStudioNotebookCount(): number {
  const { data } = useQuery(StudioNotebookCountDocument, { fetchPolicy: "cache-and-network" });
  return data?.studioNotebooks.totalCount ?? 0;
}

function toBook(node: StudioNotebookFieldsFragment): StudioNotebook {
  return {
    id: node.id,
    name: node.name ?? "",
    text: node.content ?? "",
    updatedAt: Date.parse(node.updatedAt ?? node.createdAt ?? "") || 0,
  };
}

/**
 * The person's Studio notebooks, kept in genesis as the person's own records and saved the way the match
 * editor saves (@adr SPARK-0048-studio-notebook-saves-like-the-match-editor@1): a change is on screen at once, genesis gets the
 * whole notebook behind it, one write at a time, and a status says when that is done or failed. genesis guards
 * each write with its internal version (@external-adr GENESIS-0008-optimistic-locking-internal-version). Two
 * tabs on one notebook still overwrite each other, the last save wins, as in the match editor.
 */
export function useStudioNotebooks(): StudioNotebooksState {
  const client = useApolloClient();
  const [createNotebookMutation] = useMutation(CreateStudioNotebookDocument);
  const [updateNotebook] = useMutation(UpdateStudioNotebookDocument);
  const [deleteNotebookMutation] = useMutation(DeleteStudioNotebookDocument);

  const [stored, setStored] = useState<Stored[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // Creating or deleting did not work: nothing is kept to retry, the user repeats it, and the page says so.
  const [actionFailed, setActionFailed] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  // The queued writes read this, not the rendered state: a save must send the newest change even before React
  // has rendered it.
  const storedRef = useRef<Stored[]>([]);
  const commit = useCallback((next: Stored[]) => {
    storedRef.current = next;
    setStored(next);
  }, []);
  // One write at a time, so genesis receives the changes in the order they were made.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  // Writes queued or running, and the notebooks whose last write failed: the status follows from both.
  const pending = useRef(0);
  const failed = useRef(new Set<string>());
  const settle = useCallback(() => {
    setSaveState(failed.current.size ? "error" : pending.current ? "saving" : "idle");
  }, []);

  // Every page of the person's notebooks, newest first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const books: StudioNotebook[] = [];
      let after: string | undefined;
      do {
        const { data } = await client.query({
          query: StudioNotebooksDocument,
          variables: { input: { first: PAGE, order: { updatedAt: SortOrder.Desc }, ...(after ? { after } : {}) } },
          fetchPolicy: "network-only",
        });
        const page = data?.studioNotebooks;
        books.push(...(page?.nodes ?? []).map(toBook));
        after = page?.pageInfo.hasNextPage ? page.pageInfo.endCursor ?? undefined : undefined;
      } while (after);
      if (cancelled) return;
      // A change made while the list was loading stays: it is newer than what genesis had.
      const local = new Map(storedRef.current.map((entry) => [entry.book.id, entry]));
      commit(books.map((book) => local.get(book.id) ?? { book, dirty: false }));
      setLoading(false);
    })().catch(() => {
      if (cancelled) return;
      setLoadFailed(true);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [client, commit]);

  const enqueue = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    pending.current += 1;
    settle();
    const run = queue.current.then(task).finally(() => {
      pending.current -= 1;
      settle();
    });
    queue.current = run.catch(() => undefined);
    return run;
  }, [settle]);

  // Sends the notebook as it stands now; a newer change made meanwhile is sent by its own queued save.
  const save = useCallback((id: string) => enqueue(async () => {
    const entry = storedRef.current.find((item) => item.book.id === id);
    if (!entry?.dirty) return true;
    const sent = entry.book;
    try {
      const { data } = await updateNotebook({
        variables: { id, input: { data: { name: sent.name, content: sent.text } } },
      });
      if (data?.updateStudioNotebook.__typename !== "UpdateStudioNotebookResult") throw new Error("not saved");
      commit(storedRef.current.map((item) => (item.book === sent ? { book: item.book, dirty: false } : item)));
      failed.current.delete(id);
      setLastSavedAt(Date.now());
      return true;
    } catch {
      failed.current.add(id);
      return false;
    }
  }), [commit, enqueue, updateNotebook]);

  // A change is applied here at once and saved behind it; false only where it does not apply (any more).
  const change = useCallback((id: string, update: (book: StudioNotebook) => StudioNotebook | null): Promise<boolean> => {
    const entry = storedRef.current.find((item) => item.book.id === id);
    const next = entry ? update(entry.book) : null;
    if (!entry || !next) return Promise.resolve(false);
    commit(storedRef.current.map((item) => (item.book.id === id ? { book: { ...next, updatedAt: Date.now() }, dirty: true } : item)));
    void save(id);
    return Promise.resolve(true);
  }, [commit, save]);

  const retry = useCallback(() => {
    for (const entry of storedRef.current) if (entry.dirty) void save(entry.book.id);
  }, [save]);

  // Created and deleted in genesis first: a new notebook needs genesis' id, and a deletion is confirmed before.
  const createNotebook = useCallback((name: string) => enqueue(async () => {
    if (!name.trim()) return null;
    try {
      const { data } = await createNotebookMutation({ variables: { input: { data: { name: name.trim(), content: "" } } } });
      const out = data?.createStudioNotebook;
      if (out?.__typename !== "CreateStudioNotebookResult") throw new Error("not created");
      commit([{ book: toBook(out.result), dirty: false }, ...storedRef.current]);
      setActionFailed(false);
      setLastSavedAt(Date.now());
      return out.result.id;
    } catch {
      setActionFailed(true);
      return null;
    }
  }), [commit, createNotebookMutation, enqueue]);

  const deleteNotebook = useCallback((id: string) => enqueue(async () => {
    try {
      const { data } = await deleteNotebookMutation({ variables: { input: { id } } });
      if (!data?.deleteStudioNotebook.id) throw new Error("not deleted");
      commit(storedRef.current.filter((item) => item.book.id !== id));
      failed.current.delete(id);
      setActionFailed(false);
      return true;
    } catch {
      setActionFailed(true);
      return false;
    }
  }), [commit, deleteNotebookMutation, enqueue]);

  const renameNotebook = useCallback((id: string, name: string) =>
    name.trim() ? change(id, (book) => ({ ...book, name: name.trim() })) : Promise.resolve(false), [change]);

  const saveText = useCallback((id: string, text: string) =>
    change(id, (book) => ({ ...book, text: text.trim() })), [change]);

  const editNote = useCallback((id: string, segment: number, expected: string, text: string) =>
    change(id, (book) => {
      const next = replaceNote(book.text, segment, expected, text);
      return next === null ? null : { ...book, text: next };
    }), [change]);

  const editCard = useCallback((id: string, askId: string, cardIndex: number, card: StudioCard) =>
    change(id, (book) => {
      const next = replaceCard(book.text, askId, cardIndex, card);
      return next === null ? null : { ...book, text: next };
    }), [change]);

  const appendText = useCallback((id: string, text: string) =>
    text.trim()
      ? change(id, (book) => ({ ...book, text: book.text ? `${book.text}\n\n${text.trim()}` : text.trim() }))
      : Promise.resolve(false), [change]);

  const undoAppend = useCallback((id: string, text: string) =>
    change(id, (book) => {
      const note = text.trim();
      if (book.text === note) return { ...book, text: "" };
      const tail = `\n\n${note}`;
      return note && book.text.endsWith(tail) ? { ...book, text: book.text.slice(0, -tail.length) } : null;
    }), [change]);

  const notebooks = useMemo(
    () => stored.map((entry) => entry.book).sort((a, b) => b.updatedAt - a.updatedAt),
    [stored],
  );
  return {
    notebooks,
    loading,
    error: loadFailed || actionFailed,
    writable: !loading && !loadFailed,
    saveState,
    lastSavedAt,
    retry,
    createNotebook,
    renameNotebook,
    deleteNotebook,
    saveText,
    appendText,
    undoAppend,
    editNote,
    editCard,
  };
}
